/** @category Validation Rules */
import { invariant } from '../../jsutils/invariant.mjs';
import { GraphQLError } from '../../error/GraphQLError.mjs';

/**
 * Unique input field names
 *
 * A GraphQL input object value is only valid if all supplied fields are
 * uniquely named.
 *
 * See https://spec.graphql.org/draft/#sec-Input-Object-Field-Uniqueness
 * @param context - The validation context used while checking the document.
 * @returns A visitor that reports validation errors for this rule.
 * @example
 * ```ts
 * import { buildSchema, parse, validate } from 'graphql';
 * import { UniqueInputFieldNamesRule } from 'graphql/validation';
 *
 * const schema = buildSchema(`
 *   input Filter {
 *     name: String
 *   }
 *
 *   type Query {
 *     search(filter: Filter): String
 *   }
 * `);
 *
 * const invalidDocument = parse(`
 *   { search(filter: { name: "a", name: "b" }) }
 * `);
 * const invalidErrors = validate(schema, invalidDocument, [UniqueInputFieldNamesRule]);
 *
 * invalidErrors.length; // => 1
 *
 * const validDocument = parse(`
 *   { search(filter: { name: "a" }) }
 * `);
 * const validErrors = validate(schema, validDocument, [UniqueInputFieldNamesRule]);
 *
 * validErrors; // => []
 * ```
 */
export function UniqueInputFieldNamesRule(context) {
  const knownNameStack = [];
  let knownNames = Object.create(null);
  return {
    ObjectValue: {
      enter() {
        knownNameStack.push(knownNames);
        knownNames = Object.create(null);
      },

      leave() {
        const prevKnownNames = knownNameStack.pop();
        prevKnownNames || invariant(false);
        knownNames = prevKnownNames;
      },
    },

    ObjectField(node) {
      const fieldName = node.name.value;

      if (knownNames[fieldName]) {
        context.reportError(
          new GraphQLError(
            `There can be only one input field named "${fieldName}".`,
            {
              nodes: [knownNames[fieldName], node.name],
            },
          ),
        );
      } else {
        knownNames[fieldName] = node.name;
      }
    },
  };
}
